/**
 * Super-admin — Platform Access Defaults.
 * Set/clear the default scope for each (role, resource, action) that all
 * tenants inherit unless they override via /api/access/rules.
 */
import { useEffect, useState } from "react";
import { Shield, Save, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const SCOPES = ["own", "team", "firm", "none"];
const SCOPE_LABEL = { own: "Own", team: "Team", firm: "Firm", none: "None" };

export default function AdminAccessDefaults() {
    const [data, setData] = useState(null);

    const load = async () => {
        try { const r = await api.get("/platform/access-defaults"); setData(r.data); }
        catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const setScope = async (role, resource, action, scope) => {
        try {
            await api.post("/platform/access-defaults", { role, resource, action, scope });
            toast.success(`Default ${role}.${resource}.${action} = ${scope}`);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const clearOverride = async (role, resource, action) => {
        try {
            await api.delete(`/platform/access-defaults/${role}/${resource}/${action}`);
            toast.success("Override cleared — back to built-in default");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    if (!data) return <div className="text-center text-muted-foreground text-sm py-12"><Loader2 className="w-4 h-4 inline animate-spin mr-2"/>Loading…</div>;

    const editable = data.roles.filter(r => r !== "firm_owner" && r !== "managing_partner");
    const isOverridden = (role, res, act) => !!data.overrides_only?.[role]?.[res]?.[act];

    return (
        <div className="space-y-6" data-testid="access-defaults-page">
            <div>
                <div className="overline">Super Admin</div>
                <h1 className="display-display mt-1 inline-flex items-center gap-3">
                    <Shield className="w-7 h-7 text-primary" strokeWidth={1.5}/> Platform Access Defaults
                </h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Set platform-wide defaults that every firm inherits. Individual firms can still override these via their own Settings → Access Control.
                    Cells with a gold dot indicate a super-admin override of the built-in default.
                </p>
            </div>

            <div className="bg-card border border-border overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-background text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                        <tr>
                            <th className="text-left px-4 py-2">Role</th>
                            <th className="text-left px-4 py-2">Resource</th>
                            {data.actions.map(a => <th key={a} className="text-left px-4 py-2">{a}</th>)}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {editable.flatMap(role => data.resources.map(resource => (
                            <tr key={`${role}-${resource}`} className="border-t border-border" data-testid={`pd-row-${role}-${resource}`}>
                                <td className="px-4 py-2 text-foreground whitespace-nowrap">{role.replace(/_/g, " ")}</td>
                                <td className="px-4 py-2 text-muted-foreground">{resource}</td>
                                {data.actions.map(a => {
                                    const current = data.matrix?.[role]?.[resource]?.[a] || "own";
                                    return (
                                        <td key={a} className="px-4 py-2">
                                            <div className="inline-flex items-center gap-1.5">
                                                <select value={current} onChange={e => setScope(role, resource, a, e.target.value)}
                                                    data-testid={`pd-scope-${role}-${resource}-${a}`}
                                                    className="bg-background border border-border px-2 py-1 text-xs text-foreground">
                                                    {SCOPES.map(s => <option key={s} value={s}>{SCOPE_LABEL[s]}</option>)}
                                                </select>
                                                {isOverridden(role, resource, a) && (
                                                    <button onClick={() => clearOverride(role, resource, a)} title="Clear super-admin override"
                                                        data-testid={`pd-clear-${role}-${resource}-${a}`}
                                                        className="text-primary hover:text-foreground">
                                                        <RotateCcw className="w-3 h-3"/>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                                <td></td>
                            </tr>
                        )))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
